<template>
    <div class="flex flex-col relative z-50">
        <div class="bg-(--area-highlight) text-(--area-text)">
            <div class="container">
                <div class="py-6 grid grid-cols-2 md:grid-cols-4 justify-between gap-6 items-center">
                    <div class="max-sm:row-start-2 flex gap-4 justify-between">
                        <button
                            :disabled="store.isLoading"
                            class="flex items-center gap-2 outline-none py-1"
                            @click.prevent="store.isToggled = !store.isToggled"
                        >
                            <span class="uppercase font-mono leading-none lg:text-md">{{ store.isToggled ? "Hide" : "Show" }} Filters</span>
                            <PlusIcon class="size-6" aria-hidden="true" v-if="!store.isToggled" />
                            <MinusIcon class="size-6" aria-hidden="true" v-if="store.isToggled" />
                        </button>
                        <Transition name="fade">
                            <button
                                v-show="store.showReset && isMobile"
                                @click="store.clearFilters"
                                class="uppercase! text-md py-2 px-6 bg-red text-white hover:bg-black rounded-3xl! leading-normal! fixed left-1/2 -translate-x-1/2 bottom-6"
                            >
                                Clear Filters
                            </button>
                        </Transition>
                    </div>

                    <div class="max-sm:row-start-2 max-sm:flex max-sm:justify-end md:col-span-2">
                        <LayoutSwitch label="View" v-model="store.layout" />
                    </div>

                    <div class="max-sm:col-span-2 flex gap-4 items-center w-full justify-self-end relative">
                        <Transition name="fade">
                            <button
                                v-show="store.showReset && !isMobile"
                                @click="store.clearFilters"
                                class="uppercase! py-1 px-3 bg-red text-white hover:bg-black rounded-3xl! leading-normal! absolute right-full -translate-x-4"
                            >
                                Reset
                            </button>
                        </Transition>
                        <div class="w-full relative">
                            <Autocomplete :loading="store.isLoading" :api-url="store.apiUrl" v-if="store.postType === 'event'" />
                            <input v-else type="text" v-model="store.search" placeholder="Search" class="py-4 px-6 rounded-4xl bg-white leading-[28px] w-full min-h-[62px]">

                            <div class="absolute text-black right-4 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true">
                                <div class="spinner size-6! border-2!" v-if="store.isLoading"></div>
                                <MagnifyingGlassIcon class="size-6" v-else />
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Collapse :show="store.isToggled && !isMobile" :duration="400" easing="ease-in-out">
            <div class="bg-white">
                <div class="bg-(--area-highlight)/50 py-12">
                    <div class="container">
                        <div class="grid grid-cols-3 gap-6 relative z-40">
                            <div class="grid gap-2" v-for="{ key, label, placeholder } in getPostTypeConfig(store.postType).taxonomies" :key="key">
                                <label class="title-md text-(--area-text) uppercase" :for="`${key}-label`">{{ label }}</label>
                                <multiselect
                                    :id="`${key}-label`"
                                    :model-value="selectedOptions(key)"
                                    @update:model-value="(selected) => setFilter(key, selected)"
                                    :options="facets[key]"
                                    label="label" 
                                    :placeholder="placeholder" 
                                    class="w-full max-h-[62px]!"
                                    track-by="key" 
                                    :multiple="true"
                                    :taggable="true"
                                    @tag="addTag" 
                                    selectLabel="Add" 
                                    deselectLabel="Remove"
                                >
                                    <template #option="props">
                                        <span v-html="props.option.label"></span>
                                    </template>
                                    <template v-slot:tag="props">
                                        <span class="multiselect__tag" :class=" `mstag-${ props.option.slug }` ">
                                            <span v-html="props.option.label"></span>
                                            <i
                                                tabindex="1"
                                                class="multiselect__tag-icon"
                                                @mousedown.prevent="props.remove(props.option)"
                                            ></i>
                                        </span>
                                    </template>
                                </multiselect>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Collapse>
        <Teleport :disabled="!isMobile" to="body">
            <Transition name="slide-fade">
                <div
                    v-if="store.isToggled && isMobile"
                    class="bg-(--area-highlight) text-(--area-text) w-screen h-svh flex flex-col justify-between overflow-y-auto fixed top-0 left-0 z-89"
                >
                    <div class="divide-y divide-white grid">
                        <div class="flex gap-4 justify-between items-center p-6 text-(--area-text)">
                            <h2 class="mb-0! text-(--area-text)!">Filters</h2>
                            <button @click="store.isToggled = false" class="-mr-2!">
                                <XMarkIcon class="size-10" aria-hidden="true" />
                            </button>
                        </div>
                        <CheckBoxes v-for="{ key, label, placeholder } in getPostTypeConfig(store.postType).taxonomies" v-model="filters[key]" :options="facets[key]" :label="label" />
                    </div>
                    <div class="p-6 flex gap-6 items-center justify-center">
                        <button
                            @click="store.isToggled = false"
                            class="uppercase! py-2 px-6 text-md bg-(--area-text)! text-(--area-highlight) rounded-3xl! leading-normal!"
                        >
                            See Results
                        </button>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<script setup>
import { storeToRefs } from "pinia";
import { watch, computed } from "vue";
import { useDebounceFn, useMediaQuery } from "@vueuse/core";
import { PlusIcon, MinusIcon, XMarkIcon, MagnifyingGlassIcon } from "@heroicons/vue/20/solid";
import { TAXONOMIES, getPostTypeConfig } from "@facet/utils/constants";
import Collapse from "@facet/global/Collapse.vue";
import LayoutSwitch from "@facet/global/LayoutSwitch.vue";
import CheckBoxes from "@facet/components/filters/CheckBoxes.vue";
// import FilterGroup from "@facet/components/filters/FilterGroup.vue";
import Autocomplete from "@facet/components/filters/Autocomplete.vue";
import Multiselect from "vue-multiselect";
import { useFeedStore } from "@facet/utils/store.js";

const store = useFeedStore();
const { facets, filters, search } = storeToRefs(store);

const isMobile = useMediaQuery("(max-width: 639px)");

// Search helpers
const trimmedSearch = computed(() => (search.value || "").trim());
// Allow empty string (clears search) or 3+ chars
const isSearchValid = computed(() => trimmedSearch.value.length === 0 || trimmedSearch.value.length >= 3);

// Debounced handler → updates store & fetches items
const debouncedSearch = useDebounceFn(() => {
    // Enforce 3-character minimum unless clearing the field
    if (trimmedSearch.value.length > 0 && trimmedSearch.value.length < 3) return;
    store.fetchItems();
}, 400);

// Watch input
watch(search, () => {
    if (store.isLoading || store.isMobile) return;
    // Only trigger when valid (empty or 3+ chars)
    if (isSearchValid.value) debouncedSearch();
});

// filters[key] always holds raw term ids (matches CheckBoxes + URL sync + fetchItems).
// The multiselect UI needs full { key, label } objects, so translate on the way in/out.
function selectedOptions(key) {
    const ids = filters.value[key] || [];
    return (facets.value[key] || []).filter((option) =>
        ids.some((id) => String(id) === String(option.key))
    );
}

function setFilter(key, selected) {
    filters.value[key] = selected.map((option) => option.key);
}

function addTag(newTag) {
    const tag = {
        name: newTag,
        code: newTag.substring(0, 2) + Math.floor(Math.random() * 10000000),
    };
    this.options.push(tag);
    this.value.push(tag);
}
</script>

<style>
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.3s ease-in-out;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateX(-100%);
}

.multiselect__tags {
    min-height:62px;
    border-radius: 31px;
    /* padding-left:1rem;
    padding-top:1rem; */
    padding:0 3rem 0 1.5rem;
    padding-bottom: 0;
    font-size:1rem;
    border-color:transparent;
}
.multiselect__placeholder {
    color: var(--color-grey);
    margin: 0;
    padding: 1rem 0;
    line-height:28px;
}
.multiselect__select {
    right: 0.5rem;
    top:12px;
}
.multiselect__tag {
    background-color: black;
    color:white;
    margin-bottom: 0;
    border-radius:0;
    text-transform:uppercase;
    font-size:1rem;
    display: inline-flex;
    align-items: center;
    padding:6px;
    margin-right: 0;
    padding-right:28px;
    gap:6px;
    max-width:220px;
    position:relative;
}
.multiselect__tag-icon {
    height: auto;
    line-height:1;
    display: flex;
    position: absolute;
    right:0;
    top:0;
    width:28px;
    height:28px;
    align-items: center;
    justify-content: center;
    border-radius: 0;
    margin: 0;
    background-color: black;
}
.multiselect__tag-icon:hover {
    color:white;
}
.multiselect__tag-icon:after {
    color:white;
    font-size:1rem;
}
.multiselect__option--highlight {
    /* background-color: var(--area-highlight); */
    background-color: #CEA06D;
}
.multiselect__content-wrapper {
    --tw-shadow: 0 20px 25px -5px var(--tw-shadow-color,#0000001a), 0 8px 10px -6px var(--tw-shadow-color,#0000001a);
    box-shadow: var(--tw-inset-shadow),var(--tw-inset-ring-shadow),var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow);
}
.multiselect__option {
    padding: 1rem;
    color:black;
}
.multiselect__option span {
    text-transform: uppercase;
}
.multiselect__option:after {
    top:50%;
    transform:translateY(-50%);
    background: transparent;
    color:inherit;
}
.multiselect__tags-wrap {
    display: inline-flex;
    gap:5px;
    flex-wrap:wrap;
    padding: 1rem 0;
}
.multiselect__input {
    padding: 1rem 0;
    margin-bottom: 0;
}
.multiselect__input, .multiselect__single {
    line-height:28px;
}
.multiselect__select:before {
    color:black;
    border-color: black transparent transparent;
}
.multiselect__option--highlight {
    color:inherit;
}
</style>
